/**
 * Banking Service
 *
 * API client for banking integration endpoints.
 * Handles bank account linking (Teller + Plaid), connection management, and account operations.
 */

import { fetchApi } from '@/shared/services';
import {
  Account,
  AccountRead,
  AccountUpdate,
  BankConnection,
  ExchangeRequest,
  ExchangeResponse,
  InvestmentHoldingRead,
  LinkTokenResponse,
  ManualAccountCreate,
  PaginatedResponse,
  SyncResponse
} from '@/shared/types';

// =============================================================================
// Banking Service
// =============================================================================

export const bankingService = {
  // ===========================================================================
  // Link Token + Exchange
  // ===========================================================================

  /**
   * Get a link token for initiating bank connection.
   * For Teller: returns application_id + environment.
   * For Plaid: creates a link token via the backend.
   */
  getLinkToken: (params?: { provider?: string; products?: string[] }) => {
    const searchParams = new URLSearchParams();
    if (params?.provider) searchParams.set('provider', params.provider);
    if (params?.products) {
      for (const p of params.products) {
        searchParams.append('products', p);
      }
    }
    const qs = searchParams.toString();
    return fetchApi<LinkTokenResponse>(`/banking/link/token${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },

  /**
   * Exchange provider enrollment data for a stored bank connection
   */
  exchangeEnrollment: (data: ExchangeRequest) =>
    fetchApi<ExchangeResponse>('/banking/link/exchange', {
      method: 'POST',
      body: data
    }),

  // ===========================================================================
  // Bank Connections
  // ===========================================================================

  /**
   * List bank connections, optionally filtered by perspective.
   */
  listConnections: async (params?: { user_id?: string; household?: boolean }) => {
    const searchParams = new URLSearchParams({ page_size: '100' });
    if (params?.household) searchParams.set('household', 'true');
    else if (params?.user_id) searchParams.set('user_id', params.user_id);
    const response = await fetchApi<PaginatedResponse<BankConnection>>(`/bank-connections?${searchParams}`);
    return response.items;
  },

  /**
   * Sync a bank connection to fetch latest account and transaction data
   */
  syncConnection: (connectionId: string) =>
    fetchApi<SyncResponse>(`/bank-connections/${connectionId}/sync`, {
      method: 'POST'
    }),

  /**
   * Sync all household connections (server-side orchestrated, rate-limit safe)
   */
  syncAll: () =>
    fetchApi<SyncResponse[]>('/banking/sync', { method: 'POST' }),

  /**
   * Get an Update Mode link token for repairing a broken Plaid connection.
   * The resulting token opens Plaid Link in repair mode.
   */
  getUpdateLinkToken: (connectionId: string) =>
    fetchApi<LinkTokenResponse>(`/banking/link/update-token/${connectionId}`, { method: 'POST' }),

  /**
   * Signal that an Update Mode repair completed. Clears connection error state and triggers sync.
   */
  completeUpdateMode: (connectionId: string) =>
    fetchApi<SyncResponse>(`/banking/link/update-complete/${connectionId}`, { method: 'POST' }),

  /**
   * Remove a bank connection and optionally its accounts
   */
  removeConnection: (connectionId: string) => fetchApi<void>(`/bank-connections/${connectionId}`, { method: 'DELETE' }),

  // ===========================================================================
  // Accounts
  // ===========================================================================

  /**
   * List accounts (includes hidden accounts).
   * Optionally filter by perspective: household-wide or specific user.
   */
  listAccounts: async (params?: { user_id?: string; household?: boolean }) => {
    const searchParams = new URLSearchParams({ include_hidden: 'true', page_size: '100' });
    if (params?.household) searchParams.set('household', 'true');
    else if (params?.user_id) searchParams.set('user_id', params.user_id);
    const response = await fetchApi<PaginatedResponse<Account>>(`/accounts?${searchParams}`);
    return response.items;
  },

  /**
   * Update an account (e.g., rename or hide)
   */
  updateAccount: (accountId: string, data: AccountUpdate) => fetchApi<Account>(`/accounts/${accountId}`, { method: 'PATCH', body: data }),

  getSystemAccount: () => fetchApi<AccountRead>('/accounts/system'),

  createManualAccount: (data: ManualAccountCreate) => fetchApi<AccountRead>('/accounts/', { method: 'POST', body: data }),

  /**
   * Delete an account (soft-delete). For manual accounts, transactions are
   * reassigned to the user's Unlinked Transactions system account first.
   */
  deleteAccount: (accountId: string) => fetchApi<void>(`/accounts/${accountId}`, { method: 'DELETE' }),

  // ===========================================================================
  // Investment Holdings
  // ===========================================================================

  /**
   * Get holdings for an investment account
   */
  getHoldings: (accountId: string) => fetchApi<InvestmentHoldingRead[]>(`/accounts/${accountId}/holdings`)
};
